import React, { useRef, useEffect, useCallback } from 'react';
import {
  hasTransitionSupport,
  getEffectColors,
  getVisualSettings
} from '../img-effects/core/transitionVisualConfig';

/**
 * Canvas de Overlay para Controles Visuais
 * Responsável por desenhar controles interativos como:
 * - Círculos de transição (parametrizáveis via JSON)
 * - Handles de redimensionamento
 * - Pontos de controle de efeitos
 * - Outros controles visuais futuros
 *
 * Vantagens:
 * - Performance: Não redesenha conteúdo ao mover controles
 * - Separação: UI separada do conteúdo
 * - Escalabilidade: Fácil adicionar novos controles
 * - Configurável: Cores e efeitos via JSON
 * - Zoom-aware: Círculos acompanham o zoom do canvas
 */
const ControlsOverlayCanvas = ({
  width,
  height,
  // Dados do efeito ativo
  isEffectsPanelOpen,
  effectsEditingImage,
  activeEffectType,
  effectSettings,
  effectActiveTab,
  // Posicionamento
  paperPosition,
  // Hover state
  hoveredTransitionControl,
  // Seleção
  selectedImageId,
  // 🔍 NOVO: Zoom do canvas
  zoom = 1.0
}) => {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);

  // Inicializar canvas com suporte a HiDPI
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctxRef.current = ctx;
  }, [width, height]);

  // 🎯 Desenhar círculos de transição (genérico, parametrizável e zoom-aware)
  const drawTransitionCircles = useCallback((ctx) => {
    if (!isEffectsPanelOpen || !effectsEditingImage || !activeEffectType) return;

    // 🔒 IMPORTANTE: Só desenhar se a imagem estiver selecionada
    if (!selectedImageId || effectsEditingImage.id !== selectedImageId) return;

    // 🎨 Verificar se efeito suporta transição (via JSON)
    if (!hasTransitionSupport(activeEffectType)) return;

    // Verificar se está na aba de transição
    const currentTab = effectActiveTab[activeEffectType] || 'effect';
    if (currentTab !== 'transition') return;

    const currentSettings = effectSettings[activeEffectType] || {};
    if (!currentSettings.transitionEnabled) return;

    // 🎨 Obter cores personalizadas do efeito (via JSON)
    const colors = getEffectColors(activeEffectType);
    const visualSettings = getVisualSettings();

    // Usar centro do efeito (sincronizado)
    const { transitionRadius = 0.5, transitionFeather = 0.2, centerX: effectCenterX = 0.5, centerY: effectCenterY = 0.5 } = currentSettings;

    const imageTransform = effectsEditingImage.transform;
    const imageLeft = paperPosition.x + (imageTransform.x * paperPosition.scale);
    const imageTop = paperPosition.y + (imageTransform.y * paperPosition.scale);
    const imageWidth = imageTransform.width * paperPosition.scale;
    const imageHeight = imageTransform.height * paperPosition.scale;

    // Calcular centro em pixels (direto, sem inversão)
    const centerPosX = imageLeft + (effectCenterX * imageWidth);
    const centerPosY = imageTop + (effectCenterY * imageHeight);

    // 🔍 Calcular raios para CÍRCULO PERFEITO com ZOOM aplicado
    const minDimension = Math.min(imageWidth, imageHeight);
    const outerRadius = (transitionRadius * minDimension / 2) * zoom;
    const innerRadius = outerRadius * (1.0 - transitionFeather);
    
    ctx.save();

    // Área clicável (semi-transparente) - indica que pode arrastar de qualquer lugar
    const isAreaHovered = hoveredTransitionControl?.type === 'center';
    if (isAreaHovered) {
      ctx.fillStyle = colors.areaHover;
      ctx.beginPath();
      ctx.arc(centerPosX, centerPosY, outerRadius, 0, Math.PI * 2);
      ctx.fill();
    }

    // 🎨 Círculo EXTERNO (raio da transição) - cor personalizada
    ctx.strokeStyle = colors.outerCircle;
    ctx.lineWidth = visualSettings.outerLineWidth;
    ctx.setLineDash(visualSettings.outerDashPattern);
    ctx.beginPath();
    ctx.arc(centerPosX, centerPosY, outerRadius, 0, Math.PI * 2);
    ctx.stroke();

    // 🎨 Círculo INTERNO (suavidade) - cor personalizada
    ctx.strokeStyle = colors.innerCircle;
    ctx.lineWidth = visualSettings.innerLineWidth;
    ctx.setLineDash(visualSettings.innerDashPattern);
    ctx.beginPath();
    ctx.arc(centerPosX, centerPosY, innerRadius, 0, Math.PI * 2);
    ctx.stroke();

    ctx.setLineDash([]);

    // 🎨 Ponto central com hover effect - cor personalizada
    const isCenterHovered = hoveredTransitionControl?.type === 'center';

    if (isCenterHovered) {
      ctx.shadowColor = colors.hoverGlow;
      ctx.shadowBlur = 15;
    }

    // Círculo externo (anel branco)
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.strokeStyle = colors.centerPoint;
    ctx.lineWidth = isCenterHovered ? visualSettings.controlPointLineWidthHover : visualSettings.controlPointLineWidth;
    ctx.beginPath();
    ctx.arc(centerPosX, centerPosY, isCenterHovered ? visualSettings.centerPointSizeHover : visualSettings.centerPointSize, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Círculo interno (ponto colorido)
    ctx.fillStyle = colors.centerPoint;
    ctx.beginPath();
    ctx.arc(centerPosX, centerPosY, isCenterHovered ? visualSettings.centerPointInnerSizeHover : visualSettings.centerPointInnerSize, 0, Math.PI * 2);
    ctx.fill();

    if (isCenterHovered) {
      ctx.shadowBlur = 0;
    }
    
    // 🎨 4 pontos no círculo EXTERNO - cores personalizadas
    const outerAngles = [0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2];
    outerAngles.forEach((angle) => {
      const px = centerPosX + outerRadius * Math.cos(angle);
      const py = centerPosY + outerRadius * Math.sin(angle);

      const isHovered = hoveredTransitionControl?.type === 'outer' && hoveredTransitionControl?.angle === angle;
      const pointSize = isHovered ? visualSettings.controlPointSizeHover : visualSettings.controlPointSize;

      if (isHovered) {
        ctx.shadowColor = colors.hoverGlow;
        ctx.shadowBlur = 12;
      }

      ctx.fillStyle = isHovered ? colors.outerCircle : colors.outerCircle + 'E6'; // E6 = 90% opacity
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = isHovered ? visualSettings.controlPointLineWidthHover : visualSettings.controlPointLineWidth;
      ctx.beginPath();
      ctx.arc(px, py, pointSize, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();

      if (isHovered) {
        ctx.shadowBlur = 0;
      }
    });

    // 🎨 4 pontos no círculo INTERNO - cores personalizadas
    const innerAngles = [Math.PI / 4, (3 * Math.PI) / 4, (5 * Math.PI) / 4, (7 * Math.PI) / 4];
    innerAngles.forEach((angle) => {
      const px = centerPosX + innerRadius * Math.cos(angle);
      const py = centerPosY + innerRadius * Math.sin(angle);

      const isHovered = hoveredTransitionControl?.type === 'inner' && hoveredTransitionControl?.angle === angle;
      const pointSize = isHovered ? visualSettings.controlPointSizeHover : visualSettings.controlPointSize;

      if (isHovered) {
        ctx.shadowColor = colors.hoverGlow;
        ctx.shadowBlur = 12;
      }

      ctx.fillStyle = isHovered ? colors.innerCircle : colors.innerCircle + 'E6'; // E6 = 90% opacity
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = isHovered ? visualSettings.controlPointLineWidthHover : visualSettings.controlPointLineWidth;
      ctx.beginPath();
      ctx.arc(px, py, pointSize, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();

      if (isHovered) {
        ctx.shadowBlur = 0;
      }
    });

    ctx.restore();
  }, [isEffectsPanelOpen, effectsEditingImage, activeEffectType, effectActiveTab, effectSettings, paperPosition, hoveredTransitionControl, selectedImageId, zoom]);

  // 🎨 Desenhar ponto de controle de efeitos (ponto verde)
  const drawEffectControlPoint = useCallback((ctx) => {
    if (!isEffectsPanelOpen || !effectsEditingImage || !activeEffectType) return;

    // 🔒 IMPORTANTE: Só desenhar se a imagem estiver selecionada
    if (!selectedImageId || effectsEditingImage.id !== selectedImageId) return;

    const needsPointControl = ['lensFlare', 'lensFlareOnly', 'anamorphicFlare', 'lensFlareStrong', 'lensFlareFun', 'zoomBlur', 'magicParticles', 'magicParticlesV2'].includes(activeEffectType);
    if (!needsPointControl) return;

    // Não desenhar ponto verde quando na aba de transição (usar configuração JSON)
    if (hasTransitionSupport(activeEffectType)) {
      const currentTab = effectActiveTab[activeEffectType] || 'effect';
      if (currentTab === 'transition') return;
    }
    
    const currentSettings = effectSettings[activeEffectType] || {};
    let posX, posY;
    
    if (activeEffectType === 'zoomBlur') {
      posX = currentSettings.centerX ?? 0.5;
      posY = currentSettings.centerY ?? 0.5;
    } else if (activeEffectType === 'lensFlare' || activeEffectType === 'lensFlareOnly' || activeEffectType === 'anamorphicFlare' || activeEffectType === 'lensFlareStrong' || activeEffectType === 'lensFlareFun') {
      posX = currentSettings.mouseX ?? 0.5;
      posY = currentSettings.mouseY ?? 0.5;
    } else if (activeEffectType === 'magicParticles' || activeEffectType === 'magicParticlesV2') {
      posX = currentSettings.positionX ?? 0.5;
      posY = currentSettings.positionY ?? 0.5;
    }
    
    const imageTransform = effectsEditingImage.transform;
    const imageLeft = paperPosition.x + (imageTransform.x * paperPosition.scale);
    const imageTop = paperPosition.y + (imageTransform.y * paperPosition.scale);
    const imageWidth = imageTransform.width * paperPosition.scale;
    const imageHeight = imageTransform.height * paperPosition.scale;
    
    // 🔧 UNIFICADO: Mesmo comportamento do eixo Y para todos os efeitos
    // Zoom Blur agora usa Y direto (sem inversão), igual aos círculos
    const displayPosY = (activeEffectType === 'lensFlare' || activeEffectType === 'lensFlareOnly' || activeEffectType === 'anamorphicFlare') 
      ? 1.0 - posY 
      : posY;
    
    const pointX = imageLeft + (posX * imageWidth);
    const pointY = imageTop + (displayPosY * imageHeight);
    
    const color = '#22c55e'; // Verde
    
    ctx.save();
    
    // Crosshair
    ctx.strokeStyle = color + '40';
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 5]);
    
    ctx.beginPath();
    ctx.moveTo(imageLeft, pointY);
    ctx.lineTo(imageLeft + imageWidth, pointY);
    ctx.stroke();
    
    ctx.beginPath();
    ctx.moveTo(pointX, imageTop);
    ctx.lineTo(pointX, imageTop + imageHeight);
    ctx.stroke();
    
    ctx.setLineDash([]);
    
    // Círculo externo
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(pointX, pointY, 12, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    
    // Círculo interno
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(pointX, pointY, 4, 0, Math.PI * 2);
    ctx.fill();
    
    ctx.restore();
  }, [isEffectsPanelOpen, effectsEditingImage, activeEffectType, effectSettings, paperPosition, effectActiveTab, selectedImageId]);

  // Renderizar controles quando houver mudanças
  useEffect(() => {
    const ctx = ctxRef.current;
    if (!ctx) return;

    // Limpar canvas (transparente)
    ctx.clearRect(0, 0, width, height);

    // Desenhar controles na ordem correta
    drawEffectControlPoint(ctx);
    drawTransitionCircles(ctx);

  }, [width, height, drawEffectControlPoint, drawTransitionCircles]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none"
      style={{
        zIndex: 10,
        width: `${width}px`,
        height: `${height}px`
      }}
    />
  );
};

export default ControlsOverlayCanvas;

